import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { PageHeader } from "../../components/Layout";
import { toast } from "sonner";
import { Ticket, Plus, Trash, Copy, CheckCircle, Coin } from "@phosphor-icons/react";

function randomCode(len = 8) {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let out = "";
  for (let i = 0; i < len; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out.replace(/^(.{4})/, "$1-");
}

const defaultForm = {
  code: "",
  credits: 100,
  max_uses: 1,
  expires_at: "",
  active: true,
  description: "",
};

export default function Coupons() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(defaultForm);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/coupons");
      setItems(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const total = items.length;
    const active = items.filter((c) => c.active).length;
    const redemptions = items.reduce((s, c) => s + (c.uses || 0), 0);
    const creditsIssued = items.reduce((s, c) => s + (c.credits || 0) * (c.uses || 0), 0);
    return { total, active, redemptions, creditsIssued };
  }, [items]);

  const create = async () => {
    if (!form.code.trim() || form.credits <= 0) { toast.error("Code and positive credits required"); return; }
    setCreating(true);
    try {
      await api.post("/coupons", {
        ...form,
        credits: Number(form.credits),
        max_uses: Number(form.max_uses),
        expires_at: form.expires_at || null,
      });
      toast.success("Coupon created");
      setForm(defaultForm);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setCreating(false); }
  };

  const del = async (code) => {
    if (!window.confirm(`Delete coupon ${code}?`)) return;
    try {
      await api.delete(`/coupons/${code}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const copyCode = async (c) => {
    try {
      await navigator.clipboard.writeText(c);
      setCopied(c);
      setTimeout(() => setCopied(null), 1500);
    } catch {}
  };

  return (
    <div>
      <PageHeader
        overline="Wallet admin"
        title="Coupon codes"
        subtitle="Create redeemable codes that top up your students' wallets. Set credit amount, max uses, and expiry."
      />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 border border-zinc-200 mb-6" data-testid="coupons-stats">
        <Stat label="Codes" value={stats.total} />
        <Stat label="Active" value={stats.active} tone="emerald" />
        <Stat label="Redemptions" value={stats.redemptions} />
        <Stat label="Credits issued" value={stats.creditsIssued} tone="amber" />
      </div>

      {/* Create */}
      <div className="border border-zinc-200 p-6 mb-6" data-testid="coupon-create-form">
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4 font-semibold inline-flex items-center gap-1.5">
          <Plus size={11} /> Create coupon
        </div>
        <div className="grid md:grid-cols-6 gap-3">
          <div className="md:col-span-2">
            <Label>Code</Label>
            <div className="flex gap-1">
              <input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="ABCD-1234"
                className="w-full border border-zinc-300 px-3 py-2 text-sm font-mono uppercase focus:border-zinc-900 focus:outline-none"
                data-testid="coupon-code-input"
              />
              <button
                onClick={() => setForm({ ...form, code: randomCode() })}
                type="button"
                className="border border-zinc-300 px-2 text-xs hover:bg-zinc-50"
                data-testid="coupon-gen-btn"
              >
                Auto
              </button>
            </div>
          </div>
          <div>
            <Label>Credits</Label>
            <input
              type="number"
              value={form.credits}
              onChange={(e) => setForm({ ...form, credits: e.target.value })}
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
              data-testid="coupon-credits-input"
            />
          </div>
          <div>
            <Label>Max uses</Label>
            <input
              type="number"
              value={form.max_uses}
              onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
              data-testid="coupon-uses-input"
            />
          </div>
          <div>
            <Label>Expires (optional)</Label>
            <input
              type="date"
              value={form.expires_at}
              onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
              data-testid="coupon-expires-input"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={create}
              disabled={creating}
              className="w-full bg-zinc-900 text-white py-2 text-sm font-medium hover:bg-zinc-800 disabled:opacity-50"
              data-testid="coupon-create-btn"
            >
              {creating ? "Creating…" : "Create"}
            </button>
          </div>
          <div className="md:col-span-6">
            <Label>Description (optional)</Label>
            <input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="e.g. New batch onboarding"
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
              data-testid="coupon-description-input"
            />
          </div>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-zinc-200 p-10 text-center" data-testid="coupons-empty">
          <Ticket size={36} className="mx-auto text-zinc-300 mb-3" />
          <div className="text-sm text-zinc-500">No coupons yet.</div>
        </div>
      ) : (
        <div className="border border-zinc-200 divide-y divide-zinc-200" data-testid="coupons-list">
          {items.map((c) => {
            const expired = c.expires_at && new Date(c.expires_at) < new Date();
            const exhausted = c.max_uses > 0 && c.uses >= c.max_uses;
            const inactive = !c.active || expired || exhausted;
            return (
              <div key={c.code} className={`p-5 flex items-center gap-4 ${inactive ? "opacity-60" : ""}`} data-testid={`coupon-row-${c.code}`}>
                <div className="w-10 h-10 flex items-center justify-center border border-zinc-300">
                  <Ticket size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => copyCode(c.code)}
                      className="font-mono font-bold text-base inline-flex items-center gap-1.5 hover:text-blue-700"
                      title="Copy code"
                      data-testid={`coupon-code-${c.code}`}
                    >
                      {c.code}
                      {copied === c.code ? <CheckCircle size={12} weight="fill" className="text-green-600" /> : <Copy size={12} />}
                    </button>
                    {expired && <span className="text-[10px] uppercase tracking-wider border border-red-300 bg-red-50 text-red-700 px-2 py-0.5">Expired</span>}
                    {exhausted && !expired && <span className="text-[10px] uppercase tracking-wider border border-amber-300 bg-amber-50 text-amber-700 px-2 py-0.5">Exhausted</span>}
                    {!c.active && <span className="text-[10px] uppercase tracking-wider border border-zinc-300 bg-zinc-100 text-zinc-600 px-2 py-0.5">Inactive</span>}
                  </div>
                  {c.description && <div className="text-xs text-zinc-500 mt-1">{c.description}</div>}
                  <div className="text-[11px] text-zinc-500 mt-1">
                    Uses: <span className="font-mono">{c.uses}/{c.max_uses}</span>
                    {c.expires_at && <> · Expires <span className="font-mono">{c.expires_at.slice(0, 10)}</span></>}
                    <> · Created by <span className="text-zinc-900">{c.created_by_name}</span></>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display text-xl font-bold tabular inline-flex items-center gap-1">
                    <Coin size={14} weight="fill" className="text-amber-500" /> {c.credits}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">credits</div>
                </div>
                <button
                  onClick={() => del(c.code)}
                  className="text-red-600 hover:bg-red-50 border border-red-300 px-3 py-1.5 text-xs inline-flex items-center gap-1.5"
                  data-testid={`coupon-delete-${c.code}`}
                >
                  <Trash size={12} /> Delete
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Label({ children }) {
  return <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1 font-semibold">{children}</div>;
}

function Stat({ label, value, tone = "zinc" }) {
  const tones = {
    zinc: "text-zinc-900",
    emerald: "text-emerald-700",
    amber: "text-amber-700",
  };
  return (
    <div className="p-5 border-r border-zinc-200 last:border-r-0">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 font-semibold">{label}</div>
      <div className={`font-display text-2xl font-bold tabular ${tones[tone]}`}>{value.toLocaleString()}</div>
    </div>
  );
}
